import React, { useState, useReducer, useEffect } from "react";
import { Helmet } from "react-helmet";

import { Box, Container, CssBaseline, Grid } from "@material-ui/core";
import { Card, CardHeader, CardContent, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { useDocState } from "../hooks";
import Appbar from "../components/Appbar";
import UploadButton from "../components/UploadButton";
import UploadModal from "../components/UploadModal";

const psDescription = "Please provide your most recent pay stubs.";
const dlDescription = "Must be a valid Canadian driving license. ";

const useStyles = makeStyles({
    wrapper: {
        width: "100%",
        height: "calc(100vh - 194px)",
    },
    footer: {
        borderTop: "1px solid #e1e8ed",
        backgroundColor: "#fbfbfb",
        padding: "5px 15px",
        width: "100%",
        height: 30,
    },
    container: {
        margin: "30px auto",
    },
    header: {
        backgroundColor: "#f7f8f9",
        color: "#575962",
    },
    content: {
        marginTop: 10,
    },
    grid: {
        padding: 20,
    },
    secureBadge: {
        height: 70,
        width: 160,
        marginLeft: 15,
        background: `url("/assets/clc_secure.svg") no-repeat center`,
        backgroundSize: "100% auto",
    },
});

type DocType = "Driver's License" | "Pay Stubs" | "";

export type UploadState = {
    open: boolean;
    docType: DocType;
    leadId: string;
    prefix: string;
    description: string;
};

type Action =
    | { type: "Close" }
    | {
        type: "Open";
        payload: {
            docType: DocType;
            leadId: string;
            prefix: string;
            description: string;
        };
    };

const init: UploadState = {
    open: false,
    docType: "",
    leadId: "",
    prefix: "",
    description: "",
};

function reducer(state: UploadState, action: Action): UploadState {
    switch (action.type) {
        case "Close":
            return init;
        case "Open":
            return {
                open: true,
                docType: action.payload.docType,
                leadId: action.payload.leadId,
                prefix: action.payload.prefix,
                description: action.payload.description,
            };
        default:
            throw new Error("Unknown action!");
    }
}

export type Params = { lid: string; fnm: string; lnm: string };

export interface UploadPageProps {
    params: Params;
}

const UploadPage: React.FC<UploadPageProps> = ({ params }) => {
    const [state, dispatch] = useReducer(reducer, init);
    const [showActions, setShowActions] = useState(false);
    const { dlState, psState, setLeadId, setStatus } = useDocState();

    const css = useStyles();

    useEffect(() => {
        setShowActions(!!params.fnm && !!params.lid && !!params.lnm);
        setLeadId(params.lid);
    }, [params, setLeadId]);

    const uploadDL = () => {
        dispatch({
            type: "Open",
            payload: {
                docType: "Driver's License",
                leadId: params.lid,
                prefix: `${params.lid}/drivers_license`,
                description: dlDescription,
            },
        });
    };

    const uploadPS = () => {
        dispatch({
            type: "Open",
            payload: {
                docType: "Pay Stubs",
                leadId: params.lid,
                prefix: `${params.lid}/pay_stubs`,
                description: psDescription,
            },
        });
    };

    const setUploadedStatus = () => setStatus(state.docType, true);

    const handleClose = () => dispatch({ type: "Close" });

    return (
        <>
            <CssBaseline />
            <Helmet>
                <title>CLC Document Upload</title>
            </Helmet>
            <Appbar firstName={params.fnm} lastName={params.lnm} />

            <CssBaseline />
            <div className={css.wrapper}>
                <Container maxWidth="lg" className={css.container}>
                    <Box display={showActions ? "block" : "none"}>
                        <Card>
                            <CardHeader
                                title="Action Required"
                                className={css.header}
                            />
                            <CardContent className={css.content}>
                                <Grid
                                    container
                                    direction="row"
                                    alignItems="center"
                                    justify="space-evenly"
                                    className={css.grid}
                                    spacing={4}
                                >
                                    <Grid item xs={12} sm={6} md={5}>
                                        <UploadButton
                                            label="Driver's License"
                                            status={dlState}
                                            click={uploadDL}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={5}>
                                        <UploadButton
                                            label="Pay Stubs"
                                            status={psState}
                                            click={uploadPS}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box display={showActions ? "none" : "block"}>
                        <Typography variant="h6">
                            No Action required.
                        </Typography>
                    </Box>
                </Container>
                <UploadModal
                    open={state.open}
                    prefix={state.prefix}
                    leadId={state.leadId}
                    docType={state.docType}
                    description={state.description}
                    closeHandler={handleClose}
                    setStatus={setUploadedStatus}
                />
            </div>
            <div className={css.secureBadge} />
            <div className={css.footer}>
                <Typography variant="caption">
                    Copyright © 2021 Car Loans Canada
                </Typography>
            </div>
        </>
    );
};

export default UploadPage;
